import React from "react"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import {
	AchTotalsDaily,
	AchTotalsMonthly,
	CardTotals,
	CardLimitOverrideInfo,
	CheckLimitOverrideInfo,
	CheckPaymentLimits,
	CheckPaymentTotalsDaily,
	CheckPaymentTotalsMonthly,
	CheckTotalsDaily,
	CheckTotalsMonthly,
	getAccountLimits,
	LimitOverrideType,
	WireLimits,
	WireTotalsDaily,
	WireTotalsMonthly,
	AchLimitOverrideInfo,
	CheckPaymentLimitOverrideInfo,
	WireLimitOverrideInfo,
} from "../../../resources/account"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import RangeBox from "../../RangeBox/RangeBox"
import Icon from "../../Icon/Icon"
import {IcomoonIconName} from "../../Icon/icons"
import {faQuestionCircle} from "@fortawesome/free-regular-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import ReactTooltip from "react-tooltip"
import BodyPortal from "../../BodyPortal/BodyPortal"
import Skeleton from "react-loading-skeleton"
import {AchLimits, CardLevelLimits, CheckLimits} from "../../../resources/account"
import {
	getEffectiveAchLimit,
	getEffectiveCardLimit,
	getEffectiveCheckDepositLimit,
	getEffectiveCheckPaymentLimit,
	getEffectiveWireLimit,
} from "../../../pages/Account/AccountLimitsPage/helpers"

function AccountLimitsBlockHeader({
	title,
	icon,
	infoTip = "",
}: {
	title: string
	icon: IcomoonIconName
	infoTip?: string
}) {
	return (
		<div className="limits-drawer-block-header">
			<div className="limits-drawer-block-header-icon-placeholder">
				<Icon icon={icon} size={20} />
			</div>
			<h4> {title} </h4>
			{infoTip ? (
				<div
					className="limits-drawer-block-header-info-icon-placeholder"
					data-tip={infoTip}
					data-iscapture={true}
					data-for={"limits-drawer-info-icon"}
				>
					<FontAwesomeIcon icon={faQuestionCircle} />
					<BodyPortal>
						<ReactTooltip
							arrowColor="#FFF"
							delayHide={300}
							className="limits-drawer-info-tooltip"
							place="right"
							type="light"
							effect="solid"
							id={"limits-drawer-info-icon"}
						/>
					</BodyPortal>
				</div>
			) : (
				""
			)}
		</div>
	)
}

export function AccountLimitsDescription() {
	return (
		<>
			Limits on Unit can be either hard or soft. Transactions exceeding <b>soft</b> limits are subjected to a review
			process by the Unit team, whereas transactions exceeding <b>hard</b> limits are automatically rejected.
		</>
	)
}

function AccountLimitsDescriptionWithLink() {
	return (
		<p className="limits-drawer-description">
			<AccountLimitsDescription />
			&nbsp;You can read more about account limits in the
			<a target="_blank" rel="noreferrer" href="https://guides.unit.co/limits/">
				{" "}
				<span>limits guide</span>{" "}
				<Icon icon="maximize-square-2--interface-essential" size={12} style={{position: "relative", top: "2px"}} />
			</a>
		</p>
	)
}

function AccountLimitsPending() {
	return (
		<div className="limits-drawer">
			<AccountLimitsDescriptionWithLink />
			<AccountLimitsBlockHeader title="Card Limits" icon="credit-card-money-fund--business-products" />
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<AccountLimitsBlockHeader title="Originated ACH Payments Limits " icon="money-transfer--business-products" />
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<AccountLimitsBlockHeader title="Originated ACH Payments Limits " icon="money-transfer--business-products" />
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="box">
				<table className="table is-hoverable is-fullwidth is-clickable">
					<tbody>
						<tr>
							<td>
								<Skeleton />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	)
}

function CardAccountLimits({
	limits,
	total,
	override,
}: {
	limits: CardLevelLimits
	total: CardTotals
	override?: CardLimitOverrideInfo
}) {
	return (
		<>
			<AccountLimitsBlockHeader
				title="Card Limits"
				icon="credit-card-money-fund--business-products"
				infoTip="This limit is based on the limit set in the deposit product"
			/>

			<div className="limits-drawer-block-limits">
				<RangeBox
					title="Purchases"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveCardLimit(limits.dailyPurchase, override, LimitOverrideType.CardDailyPurchase)}
					current={total.purchases}
				/>
				<RangeBox
					title="Transaction"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveCardLimit(limits.dailyCardTransaction, override, LimitOverrideType.CardDailyTransaction)}
					current={total.cardTransactions}
				/>
				<RangeBox
					title="ATM deposit"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					max={limits.dailyDeposit}
					current={total.deposits}
				/>
				<RangeBox
					title="ATM Withdrawal"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					max={limits.dailyWithdrawal}
					current={total.withdrawals}
				/>
			</div>
		</>
	)
}

function AchAccountLimits({
	limits,
	total,
	override,
}: {
	limits: AchLimits
	total: {totalsDaily: AchTotalsDaily; totalsMonthly: AchTotalsMonthly}
	override?: AchLimitOverrideInfo
}) {
	return (
		<>
			<AccountLimitsBlockHeader title="Originated ACH Payments Limits " icon="money-transfer--business-products" />

			<div className="limits-drawer-block-limits">
				<RangeBox
					title="Debit"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveAchLimit(limits.dailyDebit, override, LimitOverrideType.AchDailyDebit)}
					current={total.totalsDaily.debits}
					softLimit={limits.dailyDebitSoft}
				/>
				<RangeBox
					title="Credit"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveAchLimit(limits.dailyCredit, override, LimitOverrideType.AchDailyCredit)}
					current={total.totalsDaily.credits}
				/>
				<RangeBox
					title="Debit"
					description="(Monthly Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveAchLimit(limits.monthlyDebit, override, LimitOverrideType.AchMonthlyDebit)}
					current={total.totalsMonthly.debits}
					softLimit={limits.monthlyDebitSoft}
				/>
				<RangeBox
					title="Credit"
					description="(Monthly Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveAchLimit(limits.monthlyCredit, override, LimitOverrideType.AchMonthlyCredit)}
					current={total.totalsMonthly.credits}
				/>
			</div>
		</>
	)
}

function CheckDepositAccountLimits({
	limits,
	total,
	override,
}: {
	limits: CheckLimits
	total: {totalsDaily: CheckTotalsDaily; totalsMonthly: CheckTotalsMonthly}
	override?: CheckLimitOverrideInfo
}) {
	return (
		<>
			<AccountLimitsBlockHeader title="Check Deposits Limits" icon="check-write--business-products" />

			<div className="limits-drawer-block-limits">
				<RangeBox
					title="Check Deposits"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					current={total.totalsDaily}
					softLimit={limits.dailySoft}
					max={getEffectiveCheckDepositLimit(limits.daily, override, LimitOverrideType.CheckDepositDaily)}
				/>
				<RangeBox
					title="Check Deposits"
					description="(Monthly Limit)"
					className="limits-drawer-range-box"
					current={total.totalsMonthly}
					softLimit={limits.monthlySoft}
					max={getEffectiveCheckDepositLimit(limits.monthly, override, LimitOverrideType.CheckDepositMonthly)}
				/>
			</div>
		</>
	)
}

function WireAccountLimits({
	limits,
	total,
	override,
}: {
	limits: WireLimits
	total: {totalsDaily: WireTotalsDaily; totalsMonthly: WireTotalsMonthly}
	override?: WireLimitOverrideInfo
}) {
	return (
		<>
			<AccountLimitsBlockHeader title="Wire Limits" icon="check-write--business-products" />

			<div className="limits-drawer-block-limits">
				<RangeBox
					title="Transfers"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveWireLimit(limits.dailyTransfer, override, LimitOverrideType.WireDailyTransfer)}
					current={total.totalsDaily.transfers}
					softLimit={limits.dailyTransferSoft}
				/>
				<RangeBox
					title="Transfers"
					description="(Monthly Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveWireLimit(limits.monthlyTransfer, override, LimitOverrideType.WireMonthlyTransfer)}
					current={total.totalsMonthly.transfers}
					softLimit={limits.monthlyTransferSoft}
				/>
			</div>
		</>
	)
}

function CheckPaymentAccountLimits({
	limits,
	total,
	override,
}: {
	limits: CheckPaymentLimits
	total: {totalsDaily: CheckPaymentTotalsDaily; totalsMonthly: CheckPaymentTotalsMonthly}
	override?: CheckPaymentLimitOverrideInfo
}) {
	return (
		<>
			<AccountLimitsBlockHeader title="Check Payments Limits" icon="check-write--business-products" />

			<div className="limits-drawer-block-limits">
				<RangeBox
					title="Sent"
					description="(Daily Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveCheckPaymentLimit(limits.dailySent || 0, override, LimitOverrideType.CheckPaymentDaily)}
					current={total.totalsDaily.sent}
					softLimit={limits.dailySentSoft}
				/>

				<RangeBox
					title="Sent"
					description="(Monthly Limit)"
					className="limits-drawer-range-box"
					max={getEffectiveCheckPaymentLimit(limits.monthlySent || 0, override, LimitOverrideType.CheckPaymentMonthly)}
					current={total.totalsMonthly.sent}
					softLimit={limits.monthlySentSoft}
				/>
			</div>
		</>
	)
}

export default function AccountLimits({accountId, accessToken}: {accountId: string; accessToken: string}) {
	const accountLimits = useAsyncResult(() => getAccountLimits(accessToken, accountId))

	return (
		<AsyncResultComponent asyncResult={accountLimits} pendingComponent={<AccountLimitsPending />}>
			{({value: limits}) => {
				return (
					<div className="limits-drawer">
						<AccountLimitsDescriptionWithLink />

						<CardAccountLimits
							limits={limits.attributes.card.limits}
							total={limits.attributes.card.totalsDaily}
							override={limits.attributes.card.override}
						/>

						<AchAccountLimits
							limits={limits.attributes.ach.limits}
							total={{
								totalsDaily: {...limits.attributes.ach.totalsDaily},
								totalsMonthly: {...limits.attributes.ach.totalsMonthly},
							}}
							override={limits.attributes.ach.override}
						/>

						<CheckDepositAccountLimits
							limits={limits.attributes.checkDeposit.limits}
							total={{
								totalsDaily: limits.attributes.checkDeposit.totalsDaily,
								totalsMonthly: limits.attributes.checkDeposit.totalsMonthly,
							}}
							override={limits.attributes.checkDeposit.override}
						/>

						<CheckPaymentAccountLimits
							limits={limits.attributes.checkPayment.limits}
							total={{
								totalsDaily: limits.attributes.checkPayment.totalsDaily,
								totalsMonthly: limits.attributes.checkPayment.totalsMonthly,
							}}
							override={limits.attributes.checkPayment.override}
						/>

						<WireAccountLimits
							limits={limits.attributes.wire.limits}
							total={{
								totalsDaily: limits.attributes.wire.totalsDaily,
								totalsMonthly: limits.attributes.wire.totalsMonthly,
							}}
							override={limits.attributes.wire.override}
						/>
					</div>
				)
			}}
		</AsyncResultComponent>
	)
}
